import { Plant, TechnicalDueDiligence } from "../../table_configuration/Project"
import { PalantirTextField, PalantirDispatchedTextField, makeTextMaskNumber } from "../../../components/input/Text"
import { PalantirBooleanSelector, PalantirDatePicker, PalantirSelector, } from "../../../components/input/SelectPicker"
import { useChangeLog } from "../../../hooks/changeLog"
import { useDBViewFormValidation } from "../../../hooks/databaseViewFormValidation"
import ViewPanel from "../ViewPanel"
import { AddFloaterButtonWithPrompt, DeleteFloaterButtonWithPrompt } from "../../../components/button/FloaterButtonWithPrompt"
import { Text, Tabs, Spoiler, Divider } from "@mantine/core"
import { HelpPrompt } from "../../../components/help/HelpPrompt"
import { HelpDryBulbTemp, HelpRainfall, HelpSnowLoad, HelpWindLoad, HelpSeismic, HelpFlood, HelpWetlands, HelpBiological, HelpProjectCOD, HelpProjectFinancialClose, HelpProjectFNTP, HelpProjectTermConversion, HelpProjectFID } from "../../../components/help/precious/Columns"
import { EPCContract, OfftakeContract } from "../../table_configuration/Contract"


export default function ProjectMilestoneDatesPanelProxy(props) {

    const selectedProject = props.selectedPlant
    const epcContracts = props.data[EPCContract.buildId()].filter(x => x[EPCContract.columnSchema.plantId]===props.selectedPlantId)
    const offtakeContracts = props.data[OfftakeContract.buildId()].filter(x => x[OfftakeContract.columnSchema.plantId]===props.selectedPlantId)

    return (
        <ProjectTDDPanel
            initialProject={selectedProject}
            initialEPCContracts={epcContracts}
            initialOfftakecontracts={offtakeContracts}
            {...props}
        />
    )
}

function ProjectTDDPanel({initialProject, initialEPCContracts, initialOfftakecontracts, ...props}) {

    const [
        projects, prepareProjectLog, updateProjectLog, addToProjectLog,
        deleteFromProjectLog, bulkOpOnProjectLog, mergeAndResetProjectLog
    ] = useChangeLog(initialProject!==null ? [initialProject] : [], Plant)
    const [
        EPCContracts, prepareEPCContractLog, updateEPCContractLog, addToEPCContractLog,
        deleteFromEPCContractLog, bulkOpOnEPCContractLog, mergeAndResetEPCContractLog
    ] = useChangeLog(initialEPCContracts, EPCContract)
    const [
        OfftakeContracts, prepareOfftakeContractLog, updateOfftakeContractLog, addToOfftakeContractLog,
        deleteFromOfftakeContractLog, bulkOpOnOfftakeContractLog, mergeAndResetOfftakeContractLog
    ] = useChangeLog(initialOfftakecontracts, EPCContract)
    const [errors, addErrors, removeErrors, setErrors, verifyChangelogSubmission, resetErrors] = useDBViewFormValidation()

    // Projects length is always 1
    const project = projects[0]

    const updateProject = (update) => updateProjectLog(project, update)

    return (
        <ViewPanel
            services={props.services}
            title="Milestone Dates"
            submitUrl='/api/precious/table/project_milestone_dates'
            verifySubmit={(payload) => {
                return verifyChangelogSubmission({
                    changeLog: payload.projectLog,
                    checks: []
                }, {
                    changeLog: payload.EPCContractLog,
                    checks: []
                }, {
                    changeLog: payload.OfftakeContractLog,
                    checks: []
                })
            }}
            onSubmitSuccess={(response, requestPayload) => {
                props.handleUpdate(false)
                resetErrors()
                mergeAndResetProjectLog()
                mergeAndResetEPCContractLog()
                mergeAndResetOfftakeContractLog()
            }}
            onSubmitError={null}
            buildSubmitPayload={() => {
                return {
                    projectLog: prepareProjectLog(),
                    EPCContractLog: prepareEPCContractLog(),
                    OfftakeContractLog: prepareOfftakeContractLog()
                }
            }}
        >
            <div className="flow-vertical form-instance" style={{minWidth: "600px"}}>
                <Text weight={500} mb={10} size="xl" style={{}}>Project</Text>
                <div className="flow-horizontal" style={{marginBottom: "15px"}}>
                    <PalantirTextField
                        label="Project Name"
                        value={project[Plant.columnSchema.plantName]}
                        disabled
                        style={{flexShrink: 0, marginBottom: "5px", marginRight: "15px"}}
                    />
                    <PalantirTextField
                        label="Project Status"
                        value={project[Plant.columnSchema.stage]}
                        disabled
                        style={{flexShrink: 0, marginBottom: "5px", marginRight: "5px"}}
                    />
                </div>
                <Text size="lg" weight={500} style={{marginBottom: "10px"}}>Dates</Text>
                <Tabs orientation="vertical" variant="outline" tabPadding="xl">
                    <Tabs.Tab label="COD">
                        <CODDates
                            services={props.services}
                            project={project}
                            updateProject={updateProject}
                            updateOfftakeContractLog={updateOfftakeContractLog}
                            OfftakeContracts={OfftakeContracts}
                        />
                    </Tabs.Tab>
                    <Tabs.Tab label="Financial Close">
                        <FinancialCloseDates
                            services={props.services}
                            project={project}
                            updateProject={updateProject}
                        />
                    </Tabs.Tab>
                    <Tabs.Tab label="Term Conversion">
                        <TermConversionDates
                            services={props.services}
                            project={project}
                            updateProject={updateProject}
                        />
                    </Tabs.Tab>
                    <Tabs.Tab label="FNTP">
                        <FNTPDates
                            services={props.services}
                            EPCContracts={EPCContracts}
                            updateEPCContractLog={updateEPCContractLog}
                        />
                    </Tabs.Tab>
                    <Tabs.Tab label="FID">
                        <FIDDates
                            services={props.services}
                            project={project}
                            updateProject={updateProject}
                        />
                    </Tabs.Tab>
                    <Tabs.Tab label="Misc.">
                        <MiscDates
                            services={props.services}
                            updateOfftakeContractLog={updateOfftakeContractLog}
                            OfftakeContracts={OfftakeContracts}
                            EPCContracts={EPCContracts}
                            updateEPCContractLog={updateEPCContractLog}
                        />
                    </Tabs.Tab>
                </Tabs>
            </div>
        </ViewPanel>
    )
}

const MiscDates = ({OfftakeContracts, updateOfftakeContractLog, EPCContracts, updateEPCContractLog}) => {
    return (
        <DatesSegment>
            <Text size="md" weight={500} mb={5} underline>Offtake Contract</Text>
            {OfftakeContracts.map(contract => {
                return (
                    <div className="flow-vertical" style={{marginBottom: "15px"}}>
                        <Text size="sm" weight={500}>{contract[OfftakeContract.columnSchema.contractName]}</Text>
                        <PalantirDatePicker
                            label="PPA Cliff Date"
                            value={contract[OfftakeContract.columnSchema.ppaCliffDate]}
                            onChange={(date) => updateOfftakeContractLog(contract, {[OfftakeContract.columnSchema.ppaCliffDate]: date})}
                            //style={{flexGrow: 1}}
                        />
                    </div>
                )
            })}
            <Text size="md" weight={500} mb={5} underline>EPC Contract</Text>
            {EPCContracts.map(contract => {
                return (
                    <>
                        <div className="flow-vertical" style={{marginBottom: "15px"}}>
                            <Text size="sm" weight={500}>{contract[EPCContract.columnSchema.contractName]}</Text>
                            <PalantirDatePicker
                                label="Energization Date"
                                value={contract[EPCContract.columnSchema.energizationDate]}
                                onChange={(date) => updateEPCContractLog(contract, {[EPCContract.columnSchema.energizationDate]: date})}
                                style={{marginBottom: "5px", "paddingRight": "10px"}}
                            />
                            <PalantirDatePicker
                                label="Performance Test Start Date"
                                value={contract[EPCContract.columnSchema.performanceTestStartDate]}
                                onChange={(date) => updateEPCContractLog(contract, {[EPCContract.columnSchema.performanceTestStartDate]: date})}
                                //style={{flexGrow: 1}}
                            />
                        </div>
                    </>
                )
            })}
        </DatesSegment>
    )
}

const FIDDates = ({project, updateProject}) => {
    return (
        <DatesSegment help={<HelpProjectFID />}>
            <Text size="md" weight={500} mb={5} underline>Project</Text>
            <PalantirDatePicker
                label="Actual FID"
                value={project[Plant.columnSchema.financialInvestmentDateActual]}
                onChange={(date) => updateProject({[Plant.columnSchema.financialInvestmentDateActual]: date})}
                style={{width: "200px", marginBottom: "5px"}}
            />
            <PalantirDatePicker
                label="Target FID"
                value={project[Plant.columnSchema.financialInvestmentDateTarget]}
                onChange={(date) => updateProject({[Plant.columnSchema.financialInvestmentDateTarget]: date})}
                style={{width: "200px"}}
            />
        </DatesSegment>
    )
}

const FNTPDates = ({updateEPCContractLog, EPCContracts}) => {
    return (
        <DatesSegment help={<HelpProjectFNTP />}>
            <Text size="md" weight={500} mb={5} underline>EPC Contract</Text>
            {EPCContracts.map(contract => {
                return (
                    <div className="flow-vertical" style={{marginBottom: "10px"}}>
                        <Text size="sm" weight={500} mb={5}>{contract[EPCContract.columnSchema.contractName]}</Text>
                        <PalantirDatePicker
                            label="Actual FNTP"
                            value={contract[EPCContract.columnSchema.fntpActual]}
                            onChange={(date) => updateEPCContractLog(contract, {[EPCContract.columnSchema.fntpActual]: date})}
                            style={{marginBottom: "5px"}}
                        />
                        <PalantirDatePicker
                            label="Contractual FNTP"
                            value={contract[EPCContract.columnSchema.fntpBudgeted]}
                            onChange={(date) => updateEPCContractLog(contract, {[EPCContract.columnSchema.fntpBudgeted]: date})}
                            style={{marginBottom: "5px"}}
                        />
                        <PalantirDatePicker
                            label="Target FNTP"
                            value={contract[EPCContract.columnSchema.fntpCurrent]}
                            onChange={(date) => updateEPCContractLog(contract, {[EPCContract.columnSchema.fntpCurrent]: date})}
                            style={{}}
                        />
                    </div>
                )
            })}
        </DatesSegment>
    )
}

const TermConversionDates = ({project, updateProject}) => {
    return (
        <DatesSegment help={<HelpProjectTermConversion />}>
            <Text size="md" weight={500} mb={5} underline>Project</Text>
            <PalantirDatePicker
                label="Actual TC"
                value={project[Plant.columnSchema.termConversionActual]}
                onChange={(date) => updateProject({[Plant.columnSchema.termConversionActual]: date})}
                style={{marginBottom: "5px"}}
            />
            <PalantirDatePicker
                label="TC Deadline"
                value={project[Plant.columnSchema.termConversionDeadline]}
                onChange={(date) => updateProject({[Plant.columnSchema.termConversionDeadline]: date})}
                style={{marginBottom: "5px"}}
            />
            <PalantirDatePicker
                label="p50 TC"
                value={project[Plant.columnSchema.termConversionP50]}
                onChange={(date) => updateProject({[Plant.columnSchema.termConversionP50]: date})}
                style={{marginBottom: "5px"}}
            />
            <PalantirDatePicker
                label="p90 TC"
                value={project[Plant.columnSchema.termConversionP90]}
                onChange={(date) => updateProject({[Plant.columnSchema.termConversionP90]: date})}
                style={{marginBottom: "5px"}}
            />
        </DatesSegment>
    )
}

const FinancialCloseDates = ({project, updateProject}) => {
    return (
        <DatesSegment help={<HelpProjectFinancialClose />}>
            <Text size="md" weight={500} mb={5} underline>Project</Text>
            <PalantirDatePicker
                label="Actual FC"
                value={project[Plant.columnSchema.actualFinancialCloseDate]}
                onChange={(date) => updateProject({[Plant.columnSchema.actualFinancialCloseDate]: date})}
                style={{marginBottom: "5px"}}
            />
            <PalantirDatePicker
                label="Memo Approved FC"
                value={project[Plant.columnSchema.memoApprovedFinancialCloseDate]}
                onChange={(date) => updateProject({[Plant.columnSchema.memoApprovedFinancialCloseDate]: date})}
                style={{marginBottom: "5px"}}
            />
            <PalantirDatePicker
                label="Target FC"
                value={project[Plant.columnSchema.targetFinancialCloseDate]}
                onChange={(date) => updateProject({[Plant.columnSchema.targetFinancialCloseDate]: date})}
                style={{marginBottom: "5px"}}
            />
            <PalantirDatePicker
                label="p50 FC"
                value={project[Plant.columnSchema.baseFinancialCloseDate]}
                onChange={(date) => updateProject({[Plant.columnSchema.baseFinancialCloseDate]: date})}
                style={{marginBottom: "5px"}}
            />
            <PalantirDatePicker
                label="p90 FC"
                value={project[Plant.columnSchema.downsideFinancialCloseDate]}
                onChange={(date) => updateProject({[Plant.columnSchema.downsideFinancialCloseDate]: date})}
                style={{marginBottom: "5px"}}
            />
            
        </DatesSegment>
    )
}

const CODDates = ({project, updateProject, updateOfftakeContractLog, OfftakeContracts}) => {
    return (
        <DatesSegment help={<HelpProjectCOD />}>
            <Text size="md" weight={500} mb={5} underline>Project</Text>
            <PalantirDatePicker
                label="Actual COD"
                value={project[Plant.columnSchema.plannedCommercialOperationDate]}
                onChange={(date) => updateProject({[Plant.columnSchema.plannedCommercialOperationDate]: date})}
                style={{marginBottom: "10px"}}
            />
            <PalantirDatePicker
                label="Memo Approved COD"
                value={project[Plant.columnSchema.commercialOperationDate]}
                onChange={(date) => updateProject({[Plant.columnSchema.commercialOperationDate]: date})}
                style={{marginBottom: "5px"}}
            />
            <PalantirDatePicker
                label="Target COD"
                value={project[Plant.columnSchema.targetCommercialOperationDate]}
                onChange={(date) => updateProject({[Plant.columnSchema.targetCommercialOperationDate]: date})}
                style={{marginBottom: "5px"}}
            />
            <PalantirDatePicker
                label="p50 COD"
                value={project[Plant.columnSchema.baseCommercialOperationDate]}
                onChange={(date) => updateProject({[Plant.columnSchema.baseCommercialOperationDate]: date})}
                style={{marginBottom: "5px"}}
            />
            <PalantirDatePicker
                label="p90 COD"
                value={project[Plant.columnSchema.downsideCommercialOperationDate]}
                onChange={(date) => updateProject({[Plant.columnSchema.downsideCommercialOperationDate]: date})}
                style={{marginBottom: "5px"}}
            />
            <Text size="md" weight={500} mb={5} underline>Offtake Contract</Text>
            {OfftakeContracts.map(contract => {
                return (
                    <div className="flow-vertical" style={{marginBottom: "10px"}}>
                        <Text size="sm" weight={500}>{contract[OfftakeContract.columnSchema.contractName]}</Text>
                        <PalantirDatePicker
                            label="Guaranteed COD"
                            value={contract[OfftakeContract.columnSchema.contractualCOD]}
                            onChange={(date) => updateOfftakeContractLog(contract, {[OfftakeContract.columnSchema.contractualCOD]: date})}
                            style={{flexGrow: 1}}
                        />
                    </div>
                )
            })}
        </DatesSegment>
    )
}

const DatesSegment = ({help, ...props}) => {
    return (
        <div className="flow-horizontal">
            <div className="flow-vertical" style={{minWidth: "220px", paddingRight: "25px"}}>
                {props.children}
            </div>
            <div style={{maxWidth: "250px"}}>
                <Spoiler maxHeight={200} showLabel="Show more..." hideLabel="Hide">
                    {help}
                </Spoiler> 
            </div>
        </div>
    )
}


/*
p10 FC discrepancy
Add Offtake Cliff Date
*/